import { Eye, EyeOff } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, Input } from '@/components/ui';
import { fieldMessages, messageForError } from '@/lib/errors';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';
import { useTheme } from '@/providers/theme';

export function LoginPage() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const { locale, setLocale } = useLocale();
  const { choice, setChoice } = useTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full place-items-center p-4">
      <div className="w-full" style={{ maxInlineSize: '26rem' }}>
        <div className="mb-6 text-center">
          {/* Artwork, not a string — never translated or transliterated. */}
          <p className="dio-display text-4xl" translate="no">
            DIÓRA
          </p>
          <p className="dio-meta">{t('app.tagline')}</p>
        </div>

        <form className="dio-card" onSubmit={onSubmit} noValidate>
          <div className="dio-card__body flex flex-col gap-4">
            <div>
              <h1 className="dio-card__title">{t('auth.signInTitle')}</h1>
              <p className="dio-hint">{t('auth.signInSubtitle')}</p>
            </div>

            {error != null && (
              <div className="dio-alert dio-alert--danger" role="alert">
                <div className="dio-alert__body">{messageForError(t, error)}</div>
              </div>
            )}

            <Field
              label={t('auth.email')}
              htmlFor="email"
              required
              errors={fieldMessages(t, error, 'email')}
            >
              <Input
                id="email"
                type="email"
                name="email"
                autoComplete="username"
                dir="ltr"
                required
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Field
              label={t('auth.password')}
              htmlFor="password"
              required
              errors={fieldMessages(t, error, 'password')}
            >
              <div className="dio-input-group">
                <Input
                  id="password"
                  type={reveal ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  dir="ltr"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onClick={() => setReveal((current) => !current)}
                  aria-label={reveal ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {reveal ? <EyeOff className="dio-icon" aria-hidden="true" /> : <Eye className="dio-icon" aria-hidden="true" />}
                </Button>
              </div>
            </Field>

            <Button type="submit" variant="primary" block loading={busy}>
              {busy ? t('auth.submitting') : t('auth.submit')}
            </Button>
          </div>
        </form>

        {/* Available before signing in: someone who cannot read the English
            form has to be able to switch to Arabic to use it. */}
        <div className="mt-4 flex items-center justify-center gap-2">
          {(['ar', 'en'] as const).map((option) => (
            <button
              key={option}
              type="button"
              lang={option}
              className="dio-btn dio-btn--ghost dio-btn--sm"
              aria-pressed={locale === option}
              onClick={() => setLocale(option)}
            >
              {t(`locale.${option}`)}
            </button>
          ))}
          <span aria-hidden="true" className="dio-meta">·</span>
          {(['light', 'dark', 'system'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="dio-btn dio-btn--ghost dio-btn--sm"
              aria-pressed={choice === option}
              onClick={() => setChoice(option)}
            >
              {t(`theme.${option}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
